import moment from "moment"
import { Table } from "antd-v5"
import AntdButton from "../../../components/AntdButton"
import AntdInput from "../../../components/AntdInput"
import { Tab, Tabs } from "../../../components/Tabs"
import ComplaintModal from "./ComplaintModal"
import useAppDispatch from "../../../hooks/useAppDispatch"
import useAppSelector from "../../../hooks/useAppSelector"
import { useEffect, useMemo, useState } from "react"
import ResolveLocationComplaintModal from "./ResolveLocationComplaintModal"
import { GetLocationDetailsInterface, LocationComplaintInterface } from "../../../types/locations.types"
import { ComplaintState, DATE_FORMATS, GLOBAL_DATE_FORMAT } from "../../../config/constants"
import { PlusOutlined } from "@ant-design/icons"
import { caseConverters, convertIOSDateToHTMLFormat } from "../../../config/utils"
import { getLocationComplaints, locationComplaintsList } from "../../../store/Location/locationComplaints.slice"
import { getEmployees } from "store/Employee/detail.slice"
import {
	getEmployeeBansByLocation,
	isEmployeeBanLoading,
	selectEmployeeBanData,
} from "../../../store/Employee/Location/employeeBan.slice"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import { useLocationScheduleDefaults } from "hooks/useScheduleDefaults"
import { selectLocationById } from "store/Location/locationDetails.slice"

const Complaints: React.FC = () => {
	const [openModal, setOpenModal] = useState(false)
	const [openResolveModal, setOpenResolveModal] = useState(false)
	const [isOpenResolveModalAsReadOnly, setIsOpenResolveModalAsReadOnly] = useState(false)
	const [searchString, setSearchString] = useState<string>("")
	const dispatch = useAppDispatch()
	useLocationScheduleDefaults()

	const { selectedLocation = "" } = useSchedulesQueryParams()
	const complaintList = useAppSelector(locationComplaintsList())
	const [selectedComplaint, setSelectedComplaint] = useState<LocationComplaintInterface | null>(null)
	const location = useAppSelector(selectLocationById(selectedLocation)) as GetLocationDetailsInterface

	const employeeBans = useAppSelector(selectEmployeeBanData())
	const isLoadingEmployeeBans = useAppSelector(isEmployeeBanLoading())
	// const { fetchComplaintCommentsByComplaint, isLoading } = useComplaintComments()

	const filteredEmployeeBans = useMemo(() => {
		if (!searchString) return employeeBans

		return employeeBans.filter(ban => {
			const employeeName = ban.employee ? `${ban.employee.firstName} ${ban.employee.lastName}`.toLowerCase() : ""
			const pin = ban.employee?.pin?.toLowerCase() || ""
			const from = ban.from?.toLowerCase() || ""
			const reason = ban.reason?.toLowerCase() || ""
			const bannedFrom = ban.bannedFrom?.toLowerCase() || ""
			const search = searchString.toLowerCase()
			return (
				employeeName.includes(search) ||
				pin.includes(search) ||
				from.includes(search) ||
				reason.includes(search) ||
				bannedFrom.includes(search)
			)
		})
	}, [employeeBans, searchString])

	useEffect(() => {
		dispatch(getEmployees())
		dispatch(getLocationComplaints(selectedLocation))
		if (location) dispatch(getEmployeeBansByLocation(selectedLocation, location.branch._id, location.customer._id))
	}, [selectedLocation, location])

	// The following commented-out code pertains to report generation functionality. It is not currently needed but will be integrated in a future phase of development.

	// const handleDownloadReport = async (row: LocationComplaintInterface) => {
	// 	if (row._id) {
	// 		const data = await fetchComplaintCommentsByComplaint(row._id)
	// 		generateLocationComplaintPDF(row, data || [])
	// 	}
	// }

	return (
		<>
			<Tabs initialTab={0}>
				<Tab title="Site Complaints" hasHeader shouldOverflow={false}>
					<Table
						size="small"
						pagination={{
							position: ["bottomRight"],
							defaultPageSize: 12,
							pageSize: 12,
						}}
						dataSource={complaintList}
						columns={[
							{
								title: "Time Of Event",
								key: "date",
								dataIndex: "date",
								render: value => <div> {moment(value).format(DATE_FORMATS.DD_MM_YYYY_HH_MM)}</div>,
							},
							{
								title: "Complaint",
								key: "complaint",
								dataIndex: "complaint",
							},
							{
								title: "Status",
								key: "status",
								dataIndex: "status",
								render: value => <div> {caseConverters.titleCase(value)}</div>,
							},

							{
								title: "Action",
								key: "",
								dataIndex: "",
								render: (_, row) => (
									<>
										<div className="flex flex-row gap-x-2">
											<AntdButton
												onClick={() => {
													setSelectedComplaint(selectedRow =>
														selectedRow === row ? null : row,
													)
													setOpenModal(true)
												}}
												disabled={row.status === ComplaintState.RESOLVED}
											>
												Update
											</AntdButton>

											<AntdButton
												disabled={row.status === ComplaintState.RESOLVED}
												onClick={() => {
													setSelectedComplaint(row)
													setOpenResolveModal(true)
												}}
											>
												Resolve
											</AntdButton>
										</div>
									</>
								),
							},

							{
								title: "",
								key: "",
								dataIndex: "",
								render: (_, row) => (
									<>
										<div className="flex flex-row gap-x-2">
											<AntdButton
												onClick={() => {
													setSelectedComplaint(selectedRow =>
														selectedRow === row ? null : row,
													)
													setOpenResolveModal(true)
													setIsOpenResolveModalAsReadOnly(true)
												}}
												type="text"
												className="text-primary"
											>
												View Details
											</AntdButton>
										</div>
									</>
								),
							},

							// The following commented-out code pertains to report generation functionality. It is not currently needed but will be integrated in a future phase of development.

							// {
							// 	title: "Report",
							// 	key: "",
							// 	render: (_, row) => (
							// 		<AntdButton
							// 			className="flex items-center justify-center"
							// 			icon={<CloudDownloadOutlined />}
							// 			onClick={() => handleDownloadReport(row)}
							// 			loading={isLoading}
							// 			disabled={isLoading}
							// 		>
							// 			Download
							// 		</AntdButton>
							// 	),
							// },
						]}
					/>
					<div className="flex justify-end pt-4">
						<AntdButton icon={<PlusOutlined />} onClick={() => setOpenModal(val => !val)}>
							Add New
						</AntdButton>
					</div>
				</Tab>
				<Tab title="Employee Bans" hasHeader shouldOverflow={false}>
					<div className="mb-4">
						<AntdInput
							onChange={e => setSearchString(e.target.value)}
							value={searchString}
							placeholder="Search by name, PIN, from, or reason"
							type="text"
							className="w-[30%]"
						/>
					</div>
					<Table
						size="small"
						pagination={{
							position: ["bottomRight"],
							defaultPageSize: 12,
							pageSize: 12,
						}}
						dataSource={filteredEmployeeBans}
						loading={isLoadingEmployeeBans}
						columns={[
							{
								title: "Banned From",
								key: "bannedFrom",
								dataIndex: "bannedFrom",
								render: value => <div>{value ? caseConverters.titleCase(value) : "-"}</div>,
							},
							{
								title: "Name",
								key: "name",
								dataIndex: "employee",
								render: value => (
									<div>
										{value
											? caseConverters.titleCase(`${value?.firstName} ${value?.lastName}`)
											: "N/A"}
									</div>
								),
							},
							{
								title: "PIN",
								key: "pin",
								dataIndex: "employee",
								render: value => <div> {value ? caseConverters.titleCase(value?.pin) : "N/A"}</div>,
							},
							{
								title: "From",
								key: "from",
								dataIndex: "from",
								render: value => <div> {value ? caseConverters.titleCase(value) : "N/A"}</div>,
							},
							{
								title: "Reason",
								key: "reason",
								dataIndex: "reason",
								render: value => <div> {value ? caseConverters.titleCase(value) : "N/A"}</div>,
							},
							{
								title: "Start Date",
								key: "startDate",
								dataIndex: "startDate",
								render: value => (
									<div> {value ? convertIOSDateToHTMLFormat(value, GLOBAL_DATE_FORMAT) : "N/A"}</div>
								),
							},
							{
								title: "Finish Date",
								key: "finishDate",
								dataIndex: "finishDate",
								render: value => (
									<div> {value ? convertIOSDateToHTMLFormat(value, GLOBAL_DATE_FORMAT) : "N/A"}</div>
								),
							},
						]}
					/>
				</Tab>
			</Tabs>

			<ComplaintModal
				selectedLocation={selectedLocation ?? ""}
				open={openModal}
				modalHandler={(open: boolean) => {
					setOpenModal(open)
					setSelectedComplaint(null)
				}}
				selectedRow={null}
				defaultFields={
					selectedComplaint
						? {
								...selectedComplaint,
								officer:
									typeof selectedComplaint.officer === "object"
										? selectedComplaint.officer?._id ?? ""
										: selectedComplaint.officer ?? "",
						  }
						: undefined
				}
			/>

			<ResolveLocationComplaintModal
				modalHandler={close => {
					setOpenResolveModal(close)
					setSelectedComplaint(null)
					setIsOpenResolveModalAsReadOnly(close)
				}}
				open={openResolveModal}
				selectedComplaint={selectedComplaint}
				readonly={isOpenResolveModalAsReadOnly}
			/>
		</>
	)
}

export default Complaints
